import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, ArrowRight, ArrowUpRight, Shield, Camera, FileCheck } from "lucide-react";
import { fadeUp, stagger } from "@/data/landingData";

/**
 * Hero — first-impression section.
 *
 * H1 evolution:
 *   v1: "Staviame budúcnosť" — too abstract
 *   v2: "Váš dom na kľúč. Bez cenových prekvapení." — too narrow (only 1 service)
 *   v3 (Feb 2026): "Stavebné práce bez cenových prekvapení." — covers ALL our
 *     services (rekonštrukcie, zateplenie, kúpelne, dom na kľúč), links to
 *     online estimator + dedicated /dom-na-kluc subpage for that vertical.
 */
export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const heroBullets = [
    { icon: FileCheck, label: "Fixná cena po podpise" },
    { icon: Camera, label: "Denné foto-reporty" },
    { icon: Shield, label: "3-ročná záruka" },
  ];

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/934029/pexels-photo-934029.jpeg?auto=compress&w=1920)",
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 tech-grid opacity-30" />
      </motion.div>

      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#00E5FF]/10 rounded-full blur-[100px] animate-float" />
      <div
        className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#FF5E00]/10 rounded-full blur-[120px] animate-float"
        style={{ animationDelay: "-3s" }}
      />

      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32"
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="max-w-4xl"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8">
            <span className="mono text-xs text-[#00E5FF] uppercase tracking-[0.2em]">
              Kompletný exteriérový realizátor · Bratislava · Podunajsko · od 2009
            </span>
            <span className="w-12 h-px bg-gradient-to-r from-[#00E5FF] to-transparent" />
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[0.95] mb-6"
          >
            Stavba domu
            <br />
            <span className="text-[#00E5FF] neon-cyan">od projektu po prístupovú cestu</span>
            <span className="text-[#FF5E00]">.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="text-lg sm:text-xl text-zinc-300 max-w-2xl mb-8 font-light leading-relaxed"
          >
            Postavíme <span className="text-white font-medium">rodinný dom na kľúč</span> alebo{" "}
            <span className="text-white font-medium">holodom od 960 €/m²</span>, fasádu{" "}
            <span className="text-white font-medium">zateplíme od 48 €/m²</span> a okolie
            zrealizujeme <span className="text-white font-medium">zámkovou dlažbou od 70 €/m²</span>.
            Všetko <span className="text-[#00E5FF]">s jedným partnerom</span> — jedna zmluva,
            jedna 3-ročná záruka, jeden garant termínu.{" "}
            <a href="/dom-a-okolie-na-kluc" className="text-[#00E5FF] hover:underline font-medium">
              Prečo s jedným partnerom?
            </a>
          </motion.p>

          {/* Trust bullets - specific promises, not marketing fluff */}
          <motion.div variants={fadeUp} className="flex flex-wrap gap-x-6 gap-y-2 mb-10">
            {heroBullets.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="flex items-center gap-2" data-testid={`hero-bullet-${i}`}>
                  <Icon size={16} className="text-[#00E5FF] flex-shrink-0" />
                  <span className="text-sm text-zinc-200">{b.label}</span>
                </div>
              );
            })}
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
            <a
              href="#kontakt"
              className="group btn-primary flex items-center gap-3"
              data-testid="hero-cta"
            >
              <Sparkles size={20} />
              Získať nezáväzný plán
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href="#portfolio"
              className="btn-secondary flex items-center gap-3"
              data-testid="hero-portfolio"
            >
              Pozrieť realizácie
              <ArrowUpRight size={18} />
            </a>
          </motion.div>

          {/* Reassurance line under CTA — Emmy: reduce risk before click */}
          <motion.p
            variants={fadeUp}
            className="text-xs text-zinc-500 mt-4"
            data-testid="hero-reassurance"
          >
            Cenová ponuka do 24 h · nezáväzne · zdarma. Nevoláme opakovane.
          </motion.p>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="mono text-xs text-zinc-500 uppercase tracking-widest">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-[#00E5FF] to-transparent" />
      </div>
    </section>
  );
}
